import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { FaCrown, FaBolt, FaMedal, FaTrophy, FaStar } from "react-icons/fa";

/* ── Theme ── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  bgCard2: "#0d1f38",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  accentGlow: "rgba(234,179,8,0.3)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.12)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
  successDim: "rgba(34,197,94,0.1)",
};

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n || 0));

/* ── rank configs ── */
const RANK_CFG = [
  {
    pos: 1,
    crown: <FaTrophy size={14} />,
    gradFrom: "#92400e", gradTo: "#eab308",
    glow: "rgba(234,179,8,0.45)",
    borderColor: "rgba(234,179,8,0.5)",
    label: "GOLD",
    entranceX: -90,
    delay: 0.1,
  },
  {
    pos: 2,
    crown: <FaMedal size={14} />,
    gradFrom: "#374151", gradTo: "#9ca3af",
    glow: "rgba(156,163,175,0.35)",
    borderColor: "rgba(156,163,175,0.4)",
    label: "SILVER",
    entranceX: 0,
    entranceY: -70,
    delay: 0.25,
  },
  {
    pos: 3,
    crown: <FaMedal size={14} />,
    gradFrom: "#7c2d12", gradTo: "#c2410c",
    glow: "rgba(194,65,12,0.35)",
    borderColor: "rgba(194,65,12,0.4)",
    label: "BRONZE",
    entranceX: 90,
    delay: 0.4,
  },
];

/* ── Animated podium canvas ── */
function PodiumCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let t = 0, id;
    let W = c.offsetWidth || window.innerWidth;
    const H = 120;
    c.width = W; c.height = H;

    const handleResize = () => {
      W = c.offsetWidth || window.innerWidth;
      c.width = W;
    };
    window.addEventListener("resize", handleResize);

    function loop() {
      ctx.clearRect(0, 0, W, H);
      for (let x = 0; x < W; x += 36) {
        const wave = Math.sin((x / W) * Math.PI * 2 + t) * 6;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H + wave);
        ctx.strokeStyle = `rgba(234,179,8,${(0.02 + Math.abs(Math.sin(x + t)) * 0.03).toFixed(3)})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      for (let y = 0; y < H; y += 20) {
        ctx.beginPath();
        ctx.moveTo(0, y + Math.sin(t + y * 0.1) * 3);
        ctx.lineTo(W, y + Math.cos(t + y * 0.08) * 3);
        ctx.strokeStyle = `rgba(59,130,246,${(0.015 + Math.abs(Math.sin(y + t)) * 0.02).toFixed(3)})`;
        ctx.lineWidth = 0.4;
        ctx.stroke();
      }
      t += 0.008;
      id = requestAnimationFrame(loop);
    }
    loop();
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return (
    <canvas ref={ref} style={{
      position: "absolute", inset: 0, width: "100%", height: "100%",
      pointerEvents: "none", opacity: 0.85,
    }} />
  );
}

/* ── Top-3 tilt card ── */
function PodiumCard({ user, cfg, idx }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotX = useTransform(y, [-60, 60], [8, -8]);
  const rotY = useTransform(x, [-60, 60], [-8, 8]);

  function onMove(e) {
    const r = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - r.left - r.width / 2);
    y.set(e.clientY - r.top - r.height / 2);
  }

  const initAnim = cfg.entranceY
    ? { opacity: 0, y: cfg.entranceY, scale: 0.88 }
    : { opacity: 0, x: cfg.entranceX, scale: 0.88 };
  const visAnim = { opacity: 1, x: 0, y: 0, scale: 1 };

  return (
    <motion.div
      initial={initAnim}
      whileInView={visAnim}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: cfg.delay }}
      style={{ perspective: 900, position: "relative", width: "100%", height: "100%" }}
      onMouseMove={onMove}
      onMouseLeave={() => { x.set(0); y.set(0); }}
    >
      <motion.div
        initial={{ opacity: 0, y: -16, scale: 0.7 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: cfg.delay + 0.35, type: "spring", stiffness: 280, damping: 18 }}
        style={{
          position: "absolute", top: -18, left: "50%", transform: "translateX(-50%)",
          zIndex: 10,
          background: `linear-gradient(135deg,${cfg.gradFrom},${cfg.gradTo})`,
          color: "#fff",
          fontSize: 9, fontWeight: 900, letterSpacing: "0.22em", textTransform: "uppercase",
          padding: "5px 14px", borderRadius: 999,
          boxShadow: `0 4px 18px ${cfg.glow}`,
          display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
        }}
      >
        {cfg.crown} #{cfg.pos} {cfg.label}
      </motion.div>

      <motion.div style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d", height: "100%" }}
        whileHover={{ scale: 1.03 }}
        transition={{ type: "spring", stiffness: 280, damping: 24 }}
      >
        <div style={{
          background: T.bgCard,
          border: `1px solid ${cfg.borderColor}`,
          borderRadius: 22, 
          padding: "clamp(24px, 4vw, 28px) clamp(16px, 3vw, 20px) clamp(18px, 3vw, 22px)",
          position: "relative", overflow: "hidden", height: "100%",
          boxShadow: `0 0 0 1px ${cfg.glow.replace("0.45", "0.12")}, 0 20px 50px rgba(0,0,0,0.5), 0 0 36px ${cfg.glow}`,
        }}>
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg,${cfg.gradFrom},${cfg.gradTo})`,
          }} />
          <div style={{
            position: "absolute", top: -20, right: -20, width: 90, height: 90,
            borderRadius: "50%",
            background: `radial-gradient(ellipse,${cfg.glow} 0%,transparent 70%)`,
            pointerEvents: "none",
          }} />
          <motion.div
            initial={{ x: "-130%" }}
            whileInView={{ x: "130%" }}
            viewport={{ once: true }}
            transition={{ delay: cfg.delay + 0.5, duration: 0.9, ease: "easeInOut" }}
            style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.05) 50%,transparent 70%)",
              pointerEvents: "none", zIndex: 1,
            }}
          />
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ delay: cfg.delay + 0.2, type: "spring", stiffness: 260, damping: 16 }}
            style={{
              width: 62, height: 62, borderRadius: 18,
              background: `linear-gradient(135deg,${cfg.gradFrom},${cfg.gradTo})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 24, fontWeight: 900, color: "#fff",
              margin: "0 auto 14px",
              boxShadow: `0 6px 22px ${cfg.glow}`,
              position: "relative", zIndex: 2,
            }}
          >
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </motion.div>
          <div style={{ textAlign: "center", marginBottom: 16, position: "relative", zIndex: 2 }}>
            <div style={{
              fontSize: "0.95rem", fontWeight: 900, color: T.textPrimary,
              letterSpacing: "-0.01em", marginBottom: 6,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {user?.name || "Anonymous"}
            </div>
            <span style={{
              display: "inline-block",
              padding: "3px 10px",
              background: "rgba(255,255,255,0.05)",
              border: `1px solid ${cfg.borderColor}`,
              borderRadius: 999,
              fontSize: 9, fontWeight: 900, color: T.textSecondary,
              letterSpacing: "0.2em", textTransform: "uppercase",
            }}>
              {user?.rank?.title || "LVL 1"}
            </span>
          </div>
          <div style={{
            background: "rgba(0,0,0,0.3)",
            border: "1px solid rgba(255,255,255,0.05)",
            borderRadius: 14, padding: "12px 10px",
            textAlign: "center", position: "relative", zIndex: 2,
          }}>
            <div style={{ fontSize: 8, color: T.textMuted, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 4 }}>
              Total Earnings
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: cfg.delay + 0.5, duration: 0.5 }}
              style={{ fontSize: "clamp(1.1rem, 3vw, 1.3rem)", fontWeight: 900, color: T.textPrimary, letterSpacing: "-0.03em" }}
            >
              {money(user?.totalEarnings)}
            </motion.div>
          </div>
          <div style={{
            display: "flex", justifyContent: "center", marginTop: 12,
            position: "relative", zIndex: 2,
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "4px 12px",
              background: T.successDim,
              border: "1px solid rgba(34,197,94,0.2)",
              borderRadius: 999,
              fontSize: 10, fontWeight: 900, color: T.success,
            }}>
              <FaBolt size={8} /> LVL {user?.level ?? 0}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Main component ── */
export default function PublicTopRankers({ limit = 10 }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/leaderboard/top?limit=${limit}`,
          { credentials: "include" }
        );
        const data = await res.json();
        if (!mounted) return;
        if (!res.ok || !data?.success) throw new Error(data?.message || "Failed to load leaderboard");
        setUsers(Array.isArray(data.users) ? data.users : []);
      } catch (e) {
        if (!mounted) return;
        setErr(e.message);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [limit]);

  const top3 = useMemo(() => users.slice(0, 3), [users]);
  const rest = useMemo(() => users.slice(3), [users]);

  return (
    <section style={{
      borderRadius: 28,
      position: "relative",
      overflow: "hidden", 
      boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
      width: "100%",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 120, overflow: "hidden" }}>
        <PodiumCanvas />
      </div>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg,transparent,${T.accent},transparent)`,
      }} />
      <div style={{
        position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)",
        width: "clamp(300px, 80vw, 500px)", height: 300, borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(234,179,8,0.04) 0%,transparent 70%)",
        pointerEvents: "none",
      }} />

      <div style={{ position: "relative", zIndex: 2, padding: "clamp(24px, 4vw, 32px) clamp(16px, 4vw, 28px)" }}>

        <motion.div
          initial={{ opacity: 0, y: -24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start",
            gap: 16, marginBottom: "clamp(24px, 5vw, 40px)",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <motion.div
                animate={{ rotate: [-6, 6, -6], y: [0, -3, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                style={{ color: T.accent, fontSize: "clamp(18px, 4vw, 22px)" }}
              >
                <FaTrophy />
              </motion.div>
              <h2 style={{
                fontSize: "clamp(1.2rem, 4vw, 1.9rem)",
                fontWeight: 900, color: T.textPrimary,
                letterSpacing: "-0.04em", margin: 0,
              }}>
                Top <span style={{ color: T.accent }}>Performers</span>
              </h2>
            </div>
            <div style={{
              fontSize: 9, fontWeight: 900, color: T.textMuted,
              letterSpacing: "0.28em", textTransform: "uppercase",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <motion.span
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.4, repeat: Infinity }}
                style={{ width: 6, height: 6, borderRadius: "50%", background: T.success, display: "inline-block", flexShrink: 0 }}
              />
              Live Rankings
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, type: "spring" }}
            style={{
              padding: "6px 16px",
              background: T.accentDim,
              border: `1px solid rgba(234,179,8,0.3)`,
              borderRadius: 999,
              fontSize: 9, fontWeight: 900, color: T.accent,
              letterSpacing: "0.2em", textTransform: "uppercase",
              display: "flex", alignItems: "center", gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <FaStar size={9} /> Global Top {limit}
          </motion.div>
        </motion.div>

        {loading && (
          <div style={{ padding: "48px 0", textAlign: "center" }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
              style={{
                width: 32, height: 32, margin: "0 auto 12px",
                border: `3px solid rgba(234,179,8,0.2)`,
                borderTopColor: T.accent,
                borderRadius: "50%",
              }}
            />
            <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, letterSpacing: "0.15em" }}>
              LOADING RANKINGS...
            </div>
          </div>
        )}

        {!loading && !err && (
          <>
            {/* ── TOP 3 PODIUM CARDS (Horizontal Scroll on Mobile) ── */}
            <div style={{
              display: "flex",
              gap: "clamp(16px, 3vw, 24px)", 
              marginBottom: 32,
              paddingTop: 24,
              paddingBottom: 16, 
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}>
              {top3.map((u, idx) => (
                <div key={u._id || idx} style={{ flex: "1 1 260px", minWidth: 260 }}>
                  <PodiumCard user={u} cfg={RANK_CFG[idx]} idx={idx} />
                </div>
              ))}
            </div>

            {/* ── REST TABLE (Horizontal Scroll Wrapper) ── */}
            {rest.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.5 }}
                style={{
                  background: "rgba(0,0,0,0.25)",
                  border: `1px solid ${T.border}`,
                  borderRadius: 18, 
                  overflow: "hidden",
                  width: "100%",
                }}
              >
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                  <div style={{ minWidth: 500 }}> 
                    <div style={{
                      display: "grid", 
                      gridTemplateColumns: "60px 1fr 140px",
                      padding: "12px clamp(12px, 3vw, 20px)",
                      borderBottom: `1px solid ${T.border}`,
                      background: "rgba(0,0,0,0.2)",
                      gap: 8,
                    }}>
                      {["Rank", "Trader", "Total PNL"].map((h, i) => (
                        <div key={i} style={{
                          fontSize: 9, fontWeight: 900, color: T.textMuted,
                          textTransform: "uppercase", letterSpacing: "0.2em",
                          textAlign: i === 2 ? "right" : "left",
                        }}>
                          {h}
                        </div>
                      ))}
                    </div>

                    {rest.map((u, i) => (
                      <motion.div
                        key={u._id || i}
                        initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.05 * i, duration: 0.45 }}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "60px 1fr 140px",
                          padding: "11px clamp(12px, 3vw, 20px)",
                          borderBottom: i < rest.length - 1 ? `1px solid rgba(26,45,74,0.6)` : "none",
                          transition: "background 0.2s",
                          cursor: "default",
                          gap: 8,
                          alignItems: "center",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(234,179,8,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <div style={{ fontSize: 11, fontWeight: 900, color: T.textMuted, fontFamily: "monospace" }}>
                          {(i + 4).toString().padStart(2, "0")}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: 9,
                            background: T.blueDim,
                            border: `1px solid rgba(59,130,246,0.2)`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 900, color: T.blue,
                            flexShrink: 0,
                          }}>
                            {(u?.name || "U").slice(0, 1).toUpperCase()}
                          </div>
                          <span style={{
                            fontSize: "12px", fontWeight: 700, color: T.textSecondary,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            maxWidth: "100%",
                          }}>
                            {u?.name || "—"}
                          </span>
                        </div>

                        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <span style={{ fontSize: "12px", fontWeight: 900, color: T.accent }}>
                            {money(u?.totalEarnings)}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}

        {err && (
          <div style={{ padding: "32px 0", textAlign: "center", color: "#ef4444", fontSize: 13, fontWeight: 700 }}>
            {err}
          </div>
        )}
      </div>
    </section>
  );
}